import React from "react";

/**
 * Card surfaces available to the step cards. `accent` and `danger` exist so the review step's
 * cost card can be this primitive instead of a private copy of it — appending an override class
 * would not have worked, since Tailwind resolves conflicts by stylesheet order, not class order.
 */
export type StepSectionTone = "default" | "accent" | "danger";

const TONES: Record<StepSectionTone, { card: string; description: string }> = {
  default: {card: "border-n-500 bg-n-600/40", description: "text-n-300"},
  accent: {card: "border-brandPurple-500/40 bg-brandPurple-500/5", description: "text-n-300"},
  danger: {card: "border-red-500 bg-red-500/10", description: "text-red-500"},
};

export interface CreateProjectStepSectionProps extends Omit<React.ComponentPropsWithoutRef<"section">, "title"> {
  title: string;
  description: string;
  /** Stable DOM handle the wizard's tests select this card by. */
  testId: string;
  /** Dims and inertises the card without unmounting it, so its state survives. */
  isDisabled?: boolean;
  /** Trailing control rendered on the title row (toggle, badge, headline figure). */
  action?: React.ReactNode;
  /** Card surface. Defaults to the neutral step-card look. */
  tone?: StepSectionTone;
  className?: string;
  children?: React.ReactNode;
}

/**
 * The one card shell every create-project step is built from.
 *
 * `LanguagesStep` and `ReviewStep` each grew a private copy of this markup, which meant a
 * spacing or border tweak had to be made twice to keep the two steps looking like one flow.
 * `pointer-events-none` and `aria-disabled` are part of the contract — `LanguagesStep`'s
 * transcribe-only test asserts them. The dimming is deliberately scoped to the body rather than
 * the whole card, so a disabled section still explains why it is disabled.
 */
const CreateProjectStepSection: React.FC<CreateProjectStepSectionProps> = (
  {
    title,
    description,
    testId,
    isDisabled,
    action,
    tone = "default",
    className,
    children,
    ...sectionProps
  }: CreateProjectStepSectionProps) => (
  <section
    {...sectionProps}
    data-testid={testId}
    aria-disabled={isDisabled}
    className={`flex w-full flex-col rounded-xl border p-6 transition-colors ${TONES[tone].card} ${
      isDisabled ? "pointer-events-none" : ""
    }${className ? ` ${className}` : ""}`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col">
        <p className="text-xl font-semibold text-white text-balance">{title}</p>
        <p className={`mt-1 text-sm text-pretty ${TONES[tone].description}`}>{description}</p>
      </div>
      {action}
    </div>
    {children && (
      <div
        inert={isDisabled}
        className={`mt-4 transition-opacity duration-300 ${isDisabled ? "opacity-40" : "opacity-100"}`}
      >
        {children}
      </div>
    )}
  </section>
);

export default CreateProjectStepSection;
